import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useIsViewer } from "@/lib/permissions";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Sparkle, CalendarBlank, Clock } from "@phosphor-icons/react";

const cats = ["Warehousing", "Healthcare", "Hospitality", "Events", "Retail", "Cleaning", "Driving"];
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINS = ["00", "15", "30", "45"];

function TimePicker({ value, onChange, testid }) {
  const [h = "09", m = "00"] = (value || "09:00").split(":");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="h-11 w-full mt-1.5 rounded-md border border-slate-300 bg-white px-3 flex items-center justify-between text-sm font-semibold hover:border-slate-400 transition-colors">
          <span><Clock size={14} className="inline mr-1.5 -mt-0.5" />{value || "Select"}</span>
          <span className="text-slate-400 text-xs">▼</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label-caps mb-1">Hour</div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {HOURS.map((x) => (
                <button key={x} type="button" onClick={() => onChange(`${x}:${m}`)}
                  className={`w-full py-1.5 rounded text-sm font-semibold ${x === h ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>{x}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-caps mb-1">Min</div>
            <div className="space-y-1">
              {MINS.map((x) => (
                <button key={x} type="button" onClick={() => onChange(`${h}:${x}`)}
                  className={`w-full py-1.5 rounded text-sm font-semibold ${x === m ? "bg-blue-600 text-white" : "hover:bg-slate-100"}`}>{x}</button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CreateJob() {
  const nav = useNavigate();
  const viewer = useIsViewer();
  const [f, setF] = useState({
    title: "", category: "Warehousing", description: "",
    location: "", postcode: "", date: null, start_time: "09:00", end_time: "17:00",
    break_minutes: 30, pay_rate: 12.5, staff_required: 1,
    required_skills: "", dress_code: "", instructions: "", status: "published",
  });
  const [busy, setBusy] = useState(false);
  if (viewer) return <Navigate to="/company/jobs" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.date) return toast.error("Pick a date");
    setBusy(true);
    try {
      const iso = f.date.toISOString().slice(0, 10);
      await api.post("/company/jobs", {
        ...f, date: iso, pay_rate: Number(f.pay_rate), staff_required: Number(f.staff_required),
        break_minutes: Number(f.break_minutes),
        required_skills: f.required_skills.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Job created");
      nav("/company/jobs");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not create job"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <div className="label-caps">Post a shift</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Create new job</h1>
      </div>
      <form onSubmit={submit} className="card-tactile p-6 grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><Label className="label-caps">Job title</Label><Input data-testid="job-title" required value={f.title} onChange={(e)=>setF({...f, title: e.target.value})} className="h-11 mt-1.5" /></div>
        <div>
          <Label className="label-caps">Category</Label>
          <Select value={f.category} onValueChange={(v)=>setF({...f, category: v})}>
            <SelectTrigger data-testid="job-category" className="h-11 mt-1.5"><SelectValue/></SelectTrigger>
            <SelectContent>{cats.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-caps">Status</Label>
          <Select value={f.status} onValueChange={(v)=>setF({...f, status: v})}>
            <SelectTrigger data-testid="job-status" className="h-11 mt-1.5"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-caps">Minimum trust band</Label>
          <Select value={f.min_trust_band || "any"} onValueChange={(v)=>setF({...f, min_trust_band: v === "any" ? null : v})}>
            <SelectTrigger data-testid="job-trust-band" className="h-11 mt-1.5"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any (default)</SelectItem>
              <SelectItem value="Bronze">Bronze or higher</SelectItem>
              <SelectItem value="Silver">Silver or higher</SelectItem>
              <SelectItem value="Gold">Gold only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label className="label-caps">Description</Label>
          <div className="flex items-center gap-2 mt-1.5 mb-1.5">
            <input data-testid="ai-brief-input" placeholder="e.g. 'Warehouse pickers for 8-hr day shift, uniform provided'"
              className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-md"
              onChange={(e)=>setF({...f, _brief: e.target.value})} value={f._brief || ""} />
            <Button type="button" data-testid="ai-draft-btn" variant="outline" onClick={async () => {
              if (!(f._brief || "").trim()) { toast.error("Enter a brief"); return; }
              try {
                const r = await api.post("/company/jobs/draft", { brief: f._brief });
                setF({ ...f, description: r.data.description });
                toast.success("AI draft ready — edit if needed");
              } catch (e) { toast.error(e.response?.data?.detail || "Draft failed"); }
            }}><Sparkle size={14} weight="fill" className="mr-1 text-yellow-500"/> AI draft</Button>
          </div>
          <Textarea data-testid="job-desc" required rows={4} value={f.description} onChange={(e)=>setF({...f, description: e.target.value})} className="mt-1.5" />
        </div>
        <div><Label className="label-caps">Location</Label><Input data-testid="job-location" required value={f.location} onChange={(e)=>setF({...f, location: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Postcode</Label><Input data-testid="job-postcode" value={f.postcode} onChange={(e)=>setF({...f, postcode: e.target.value})} className="h-11 mt-1.5" /></div>
        <div>
          <Label className="label-caps">Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid="job-date" className="h-11 w-full mt-1.5 rounded-md border border-slate-300 bg-white px-3 flex items-center justify-between text-sm font-semibold hover:border-slate-400 transition-colors">
                <span><CalendarBlank size={14} className="inline mr-1.5 -mt-0.5" />{f.date ? f.date.toISOString().slice(0,10) : "Select date"}</span>
                <span className="text-slate-400 text-xs">▼</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={f.date} onSelect={(d)=>setF({...f, date: d})} disabled={(d)=> d < new Date(new Date().setHours(0,0,0,0))} />
            </PopoverContent>
          </Popover>
        </div>
        <div><Label className="label-caps">Staff required</Label><Input data-testid="job-staff-required" required type="number" min="1" value={f.staff_required} onChange={(e)=>setF({...f, staff_required: e.target.value})} className="h-11 mt-1.5" /></div>
        <div>
          <Label className="label-caps">Start time</Label>
          <TimePicker value={f.start_time} onChange={(v)=>setF({...f, start_time: v})} testid="job-start" />
        </div>
        <div>
          <Label className="label-caps">End time</Label>
          <TimePicker value={f.end_time} onChange={(v)=>setF({...f, end_time: v})} testid="job-end" />
        </div>
        <div><Label className="label-caps">Break (min)</Label><Input data-testid="job-break" type="number" value={f.break_minutes} onChange={(e)=>setF({...f, break_minutes: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Pay rate (£/hr)</Label><Input data-testid="job-pay" required type="number" step="0.5" value={f.pay_rate} onChange={(e)=>setF({...f, pay_rate: e.target.value})} className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2"><Label className="label-caps">Required skills (comma separated)</Label><Input data-testid="job-skills" value={f.required_skills} onChange={(e)=>setF({...f, required_skills: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Dress code</Label><Input data-testid="job-dresscode" value={f.dress_code} onChange={(e)=>setF({...f, dress_code: e.target.value})} className="h-11 mt-1.5" /></div>
        <div><Label className="label-caps">Instructions</Label><Input data-testid="job-instructions" value={f.instructions} onChange={(e)=>setF({...f, instructions: e.target.value})} className="h-11 mt-1.5" /></div>
        <div className="md:col-span-2">
          <Button data-testid="job-submit-btn" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 rounded-md h-12 font-semibold">
            {busy ? "Creating…" : "Publish job"}
          </Button>
        </div>
      </form>
    </div>
  );
}
